/**
 * Attack Timeline Visualization Component
 * 
 * PHASE 5 IMPLEMENTATION
 * Shows temporal sequence of entity events in chronological order.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  Avatar,
} from '@mui/material';
import {
  Person as UserIcon,
  Computer as ProcessIcon,
  InsertDriveFile as FileIcon,
  Storage as HostIcon,
  Language as NetworkIcon,
  Shield as SecurityIcon,
  Circle as DotIcon,
} from '@mui/icons-material';
import { graphApi, TimelineEvent } from '../../../shared/services/graphApi';

interface AttackTimelineViewProps {
  alertId: string;
}

export const AttackTimelineView: React.FC<AttackTimelineViewProps> = ({ alertId }) => {
  const theme = useTheme();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchInProgress = React.useRef<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const fetchTimeline = async () => {
      if (!alertId) {
        return;
      }

      // Prevent duplicate fetches in StrictMode
      if (fetchInProgress.current) {
        return;
      }

      fetchInProgress.current = true;
      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await graphApi.getTimeline(alertId);
        
        // NULL safety check
        if (!response || !response.events) {
          throw new Error('Invalid response from timeline API');
        }

        // Events are already sorted by timestamp from backend
        if (isMounted) {
          setEvents(response.events);
        }
      } catch (err) {
        console.error('Failed to load timeline:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load timeline');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
        fetchInProgress.current = false;
      }
    };

    fetchTimeline();

    return () => {
      isMounted = false;
    };
  }, [alertId]);

  const getEventIcon = (entityType: string) => {
    switch (entityType.toLowerCase()) {
      case 'user':
        return <UserIcon />;
      case 'process':
        return <ProcessIcon />;
      case 'file':
        return <FileIcon />;
      case 'device':
      case 'host':
        return <HostIcon />;
      case 'network':
      case 'ip_address':
        return <NetworkIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  const getEventColor = (severity?: string, riskScore?: number): 'error' | 'warning' | 'info' | 'success' => {
    if (severity) {
      const sev = severity.toLowerCase();
      if (sev === 'critical' || sev === 'high') return 'error';
      if (sev === 'medium') return 'warning';
      if (sev === 'low') return 'info';
    }
    
    if (riskScore) {
      if (riskScore > 0.7) return 'error';
      if (riskScore > 0.4) return 'warning';
      return 'info';
    }
    
    return 'info';
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading timeline...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (events.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Timeline Events
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No events found for this alert
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Attack Timeline
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Chronological sequence of entity events
      </Typography>

      <Box sx={{ position: 'relative', pl: 4 }}>
        {/* Vertical line */}
        <Box
          sx={{
            position: 'absolute',
            left: 16,
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: theme.palette.divider,
          }}
        />

        {events.map((event, index) => {
          const eventColor = getEventColor(event.severity, event.risk_score);
          const colorValue = theme.palette[eventColor].main;

          return (
            <Box key={event.id} sx={{ position: 'relative', mb: 3 }}>
              {/* Timeline dot with icon */}
              <Avatar
                sx={{
                  position: 'absolute',
                  left: -28,
                  top: 8,
                  width: 32,
                  height: 32,
                  backgroundColor: colorValue,
                }}
              >
                {getEventIcon(event.entity_type)}
              </Avatar>

              {/* Event content */}
              <Box sx={{ ml: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                  {formatTimestamp(event.timestamp)}
                </Typography>

                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.background.paper,
                    borderLeft: `4px solid ${colorValue}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {event.entity_name}
                    </Typography>
                    <Chip
                      label={event.entity_type}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  </Box>

                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    {event.title}
                  </Typography>

                  {event.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {event.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {event.action && (
                      <Chip
                        label={event.action}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    )}
                    {event.event_category && (
                      <Chip
                        label={event.event_category}
                        size="small"
                        sx={{ height: 20 }}
                      />
                    )}
                    {event.risk_score && event.risk_score > 0.7 && (
                      <Chip
                        label={`Risk: ${(event.risk_score * 100).toFixed(0)}%`}
                        size="small"
                        color="error"
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
